import { FC, useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
// Types
import { ContentItemType, ContentType } from 'app/types/ContentItem';
// Models
import IReportPreset from 'app/models/ReportPreset';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getGlobalTemplates } from 'app/store/Templates/Templates.async';
import { createReport } from 'app/store/CaseReports/CaseReports.async';
import { getTeams } from 'app/store/Teams/Teams.async';
import { getPresets, createPreset, updatePreset } from 'app/store/ReportPresets/ReportPresets.async';
// Actions
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
import { TemplatesActions } from 'app/store/Templates/Templates.slice';
// Selectors
import { selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
// Mui
import { Box } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import CaseInfoBar from 'app/components/CaseInfoBar';
// Context
import ReportPresetProvider from 'app/context/ReportPreset.context';
// 
import DialogTitle from './DialogTitle';
import DialogContainer from './DialogContainer';
import { generateContentItem, parseContentItem } from 'app/utilities/ContentItemUtils';

interface IFormData {
  name: string;
  headerTemplateId: string;
  footerTemplateId: string;
  // templateIds: number[];
  content: ContentItemType[];
  watermark: {
    text: string;
    tiled: boolean;
  };
  teamIds: any;
}

type Props = {
  open: boolean;
  onClose: () => void;
  preset?: IReportPreset;
}

const ReportPresetFormDialog:FC<Props> = ({
  // Props
  open, onClose, preset
}) => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const insuranceCase = useAppSelector(selectInsuranceCase);

  const getReportContent = useMemo(() => {
    if ( !preset || insuranceCase ) return [];
    return preset.templateIds.map((id) => generateContentItem(ContentType.Template, id));;
  }, [insuranceCase, preset]);

  const methods = useForm<IFormData>({
    defaultValues: {
      name: preset?.name || '',
      headerTemplateId: preset?.headerTemplateId ? preset?.headerTemplateId.toString() : '0',
      footerTemplateId: preset?.footerTemplateId ? preset?.footerTemplateId.toString() : '0',
      content: getReportContent,
      watermark: {
        text: 'CaseChronology.com',
        tiled: true
      },
      teamIds: preset?.teamIds || []
    }
  });

  const { handleSubmit, register } = methods;

  const onSubmit = handleSubmit(async (data:IFormData) => {
    const { content, headerTemplateId, footerTemplateId, watermark, teamIds, ...otherData } = data;

    if ( !content || !content.length ) return;

    const newReportData:any = { ...otherData };
    if ( headerTemplateId && headerTemplateId !== '0' ) newReportData['headerTemplateId'] = Number(headerTemplateId);
    if ( footerTemplateId && footerTemplateId !== '0' ) newReportData['footerTemplateId'] = Number(footerTemplateId);
    if ( teamIds && teamIds.length ) newReportData['teamIds'] = teamIds;

    if ( insuranceCase ){
      newReportData['content'] = content;
      newReportData['insuranceCaseId'] = insuranceCase.id;
      if ( watermark.text ) newReportData['watermark'] = watermark;
      asyncCreateReport(newReportData);
    } else {
      newReportData['templateIds'] = content.map((contentItem:ContentItemType) => {
        const [,id] = parseContentItem(contentItem);
        return id;
      });
      if ( preset ){
        asyncUpdatePreset(preset.id, newReportData);
      } else {
        asyncCreatePreset(newReportData);
      } 
    }
  });

  const asyncCreateReport = async (data:any) => {
    try {
      await dispatch(createReport(data)).unwrap();
      onClose();
    } catch(error){}
  }

  const asyncCreatePreset = async (data:any) => {
    try {
      await dispatch(createPreset(data)).unwrap();
      onClose();
    } catch(error){}
  }

  const asyncUpdatePreset = async (presetId:number, data:any) => {
    try {
      await dispatch(updatePreset({ presetId, data })).unwrap();
      onClose();
    } catch(error){}
  }

  useEffect(() => {
    dispatch(getGlobalTemplates({}));

    // By `insuranceCase` we check is report global or not
    if ( insuranceCase ){
      const teamId:number | undefined = insuranceCase?.team?.id;
      dispatch(getPresets({ teamId }));
    } else {
      dispatch(getTeams({}));
    }

    if ( preset ){
      dispatch(MixpanelActions.handleReportPresetOpened({ presetId: preset.id }))
    }

    return () => {
      dispatch(TemplatesActions.setInitialField('globalTemplates'));
      dispatch(TemplatesActions.setInitialField('filter'));
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { border: 0 } }}
      ContentProps={{ sx: { border: 0, p: 0 } }}
    >
      <FormProvider {...methods}>
        <ReportPresetProvider
          initialContent={getReportContent}
          caseId={insuranceCase?.id}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              position: 'relative'
            }}
            component="form"
            onSubmit={onSubmit}
            noValidate
          >
            <input {...register('content') as any} type="hidden" />

            <DialogTitle onClose={onClose} preset={preset} />
            <CaseInfoBar />
            <DialogContainer />
          </Box>
        </ReportPresetProvider>
      </FormProvider>
    </Dialog>
  )
}

export default ReportPresetFormDialog;
